import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/InformationClient.css";
import {
  checkClientByEmail,
  registerClient,
  loginClient,
  getClientByEmail,
} from "../services/clientService";
import { Eye, EyeOff } from "lucide-react";

function InformationsClient() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const modeSimple = params.get("simple") === "1";

  const [mode, setMode] = useState("connexion"); // 'connexion' ou 'inscription'
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [lang, setLang] = useState(localStorage.getItem("lang") || "fr");

  useEffect(() => {
    const updateLang = () => setLang(localStorage.getItem("lang") || "fr");
    window.addEventListener("langChanged", updateLang);
    return () => window.removeEventListener("langChanged", updateLang);
  }, []);

  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    telephone: "",
    adresse: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const validatePhone = (phone) => /^[0-9]{10}$/.test(phone);

  // 🔤 Traductions FR / MG
  const t = {
    etapes: [
      lang === "fr" ? "✔ Vérifier la commande" : "✔ Hamarino ny kaomandinao",
      lang === "fr" ? "Adresse" : "Adiresy",
      lang === "fr" ? "Confirmer la commande" : "Manamafy ny kaomandinao",
    ],
    titreConnexion: lang === "fr" ? "Connexion client" : "Fidirana mpanjifa",
    titreInscription: lang === "fr" ? "Inscription client" : "Fisoratana anarana mpanjifa",
    msgTelephone: lang === "fr" ? "Le numéro de téléphone doit contenir exactement 10 chiffres." : "Tokony ho 10 ny isa ny laharana finday.",
    msgPasswordMismatch: lang === "fr" ? "Les mots de passe ne correspondent pas." : "Tsy mitovy ny tenimiafina.",
    msgEmailPasswordReq: lang === "fr" ? "Email et mot de passe requis." : "Ilaina ny mailaka sy tenimiafina.",
    msgInvalidLogin: lang === "fr" ? "Email ou mot de passe incorrect." : "Diso ny mailaka na tenimiafina.",
    msgImpossibleRecup: lang === "fr" ? "Impossible de récupérer les informations utilisateur après connexion." : "Tsy azo alaina ny mombamomba ny mpanjifa rehefa avy nanao fidirana.",
    msgServerError: lang === "fr" ? "Erreur serveur. Réessayer plus tard." : "Hadisoana serveur. Andramo indray avy eo.",
    placeholderNom: lang === "fr" ? "Nom" : "Anarana",
    placeholderPrenom: lang === "fr" ? "Prénom" : "Fanampiny",
    placeholderTelephone: lang === "fr" ? "Téléphone (10 chiffres)" : "Finday (isa 10)",
    placeholderAdresse: lang === "fr" ? "Adresse" : "Adiresy",
    placeholderEmail: lang === "fr" ? "Email" : "Mailaka",
    placeholderPassword: lang === "fr" ? "Mot de passe" : "Tenimiafina",
    placeholderConfirmPassword: lang === "fr" ? "Confirmer le mot de passe" : "Hamafiso ny tenimiafina",
    traitement: lang === "fr" ? "Traitement..." : "Miasa...",
    boutonConnexion: lang === "fr" ? "Se connecter" : "Midira",
    boutonCreerCompte: lang === "fr" ? "Créer un compte" : "Mametraha kaonty",
    pasDeCompte: lang === "fr" ? "Vous n'avez pas de compte ?" : "Tsy manana kaonty ve ianao ?",
    inscrivezVous: lang === "fr" ? "Inscrivez-vous ici" : "Misoratra anarana eto",
    dejaCompte: lang === "fr" ? "Vous avez déjà un compte ?" : "Efa manana kaonty ve ianao ?",
    connectezVous: lang === "fr" ? "Connectez-vous ici" : "Midira eto",
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setMessage("");

    try {
      if (mode === "inscription") {
        if (!validatePhone(formData.telephone)) {
          setMessage(t.msgTelephone);
          setIsSubmitting(false);
          return;
        }
        if (formData.password !== formData.confirmPassword) {
          setMessage(t.msgPasswordMismatch);
          setIsSubmitting(false);
          return;
        }

        await registerClient({
          nom: formData.nom,
          prenom: formData.prenom,
          telephone: formData.telephone,
          adresse: formData.adresse,
          email: formData.email,
          password: formData.password,
        });

        alert(lang === "fr"
          ? "✅ Inscription réussie ! Un email de vérification a été envoyé."
          : "✅ Nahomby ny fisoratana anarana ! Nalefa ny mailaka fanamarinana."
        );
        navigate("/");
        return;
      }

      // Connexion
      if (!formData.email || !formData.password) {
        setMessage(t.msgEmailPasswordReq);
        setIsSubmitting(false);
        return;
      }

      const loginResult = await loginClient({
        email: formData.email,
        password: formData.password,
      });

      let clientObj = null;
      if (!loginResult) {
        setMessage(t.msgInvalidLogin);
        setIsSubmitting(false);
        return;
      }
      if (loginResult.data) clientObj = loginResult.data.client || loginResult.data;
      else if (loginResult.client) clientObj = loginResult.client;
      else if (typeof loginResult === "object") clientObj = loginResult;

      if (!clientObj || !clientObj.email) {
        try {
          const getRes = await getClientByEmail(formData.email);
          clientObj = getRes.data || getRes;
        } catch (err) {}
      }

      if (!clientObj || !clientObj.email) {
        setMessage(t.msgImpossibleRecup);
        setIsSubmitting(false);
        return;
      }

      localStorage.setItem("clientConnecte", JSON.stringify(clientObj));
      window.dispatchEvent(new Event("clientUpdated"));

      if (modeSimple) navigate("/");
      else navigate("/paiement");

    } catch (err) {
      console.error("Erreur connexion/inscription :", err);
      setMessage(t.msgServerError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="informations-client">
      {!modeSimple && (
        <div className="etapes-panier">
          {t.etapes.map((etape, idx) => (
            <div
              key={idx}
              className={`etape ${idx === 1 ? "active" : "clickable"}`}
              onClick={() => idx === 0 && navigate("/panier")}
              title={lang === "fr" ? "Retour à la commande" : "Miverina any amin'ny baiko"}
            >
              {etape}
            </div>
          ))}
        </div>
      )}

      <h2>{mode === "connexion" ? t.titreConnexion : t.titreInscription}</h2>

      {message && (
        <div style={{ color: "red", marginBottom: 12 }} role="alert">
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {mode === "inscription" && (
          <>
            <input
              type="text"
              name="nom"
              placeholder={t.placeholderNom}
              onChange={handleChange}
              required
              autoComplete="family-name"
              aria-required="true"
            />
            <input
              type="text"
              name="prenom"
              placeholder={t.placeholderPrenom}
              onChange={handleChange}
              required
              autoComplete="given-name"
            />
            <input
              type="text"
              name="telephone"
              placeholder={t.placeholderTelephone}
              onChange={handleChange}
              onKeyPress={(e) => {
                if (!/[0-9]/.test(e.key)) e.preventDefault();
              }}
              maxLength="10"
              required
              autoComplete="tel"
            />
            <input
              type="text"
              name="adresse"
              placeholder={t.placeholderAdresse}
              onChange={handleChange}
              required
              autoComplete="street-address"
            />
          </>
        )}

        <input
          type="email"
          name="email"
          placeholder={t.placeholderEmail}
          onChange={handleChange}
          required
          autoComplete="email"
        />

        <div className="password-wrapper" style={{ position: "relative" }}>
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            placeholder={t.placeholderPassword}
            onChange={handleChange}
            required
            autoComplete={mode === "inscription" ? "new-password" : "current-password"}
            aria-required="true"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? t.placeholderConfirmPassword : t.placeholderPassword}
            style={{ background: "transparent", border: "none", cursor: "pointer", position: "absolute", right: 8, top: 8 }}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        {mode === "inscription" && (
          <div className="password-wrapper" style={{ position: "relative" }}>
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              placeholder={t.placeholderConfirmPassword}
              onChange={handleChange}
              required
              autoComplete="new-password"
              aria-required="true"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? t.placeholderPassword : t.placeholderConfirmPassword}
              style={{ background: "transparent", border: "none", cursor: "pointer", position: "absolute", right: 8, top: 8 }}
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        )}

        <button type="submit" className="payer-button" disabled={isSubmitting}>
          {isSubmitting ? t.traitement : mode === "connexion" ? t.boutonConnexion : t.boutonCreerCompte}
        </button>
      </form>

      <p style={{ marginTop: "1rem" }}>
        {mode === "connexion" ? (
          <>
            {t.pasDeCompte}{" "}
            <span style={{ color: "green", cursor: "pointer" }} onClick={() => setMode("inscription")}>
              {t.inscrivezVous}
            </span>
          </>
        ) : (
          <>
            {t.dejaCompte}{" "}
            <span style={{ color: "green", cursor: "pointer" }} onClick={() => setMode("connexion")}>
              {t.connectezVous}
            </span>
          </>
        )}
      </p>
    </div>
  );
}

export default InformationsClient;
